import React from 'react';
import Banner from './Banner';
import { MAX_CONCURRENT_CREATIVES, type OverCapRange } from './creativeConcurrency';

interface ConcurrencyBannerProps {
  overCapRanges: OverCapRange[];
  // Extra body line — used by Edit Commercial to flag a pre-existing over-cap
  // state that already existed when the modal opened.
  prefix?: React.ReactNode;
  className?: string;
}

export const ConcurrencyBanner: React.FC<ConcurrencyBannerProps> = ({
  overCapRanges,
  prefix,
  className,
}) => {
  if (overCapRanges.length === 0) return null;
  return (
    <Banner tone="caution" label="Too many commercials" className={className}>
      {prefix && <p className="mb-1">{prefix}</p>}
      <p>
        Up to <strong>{MAX_CONCURRENT_CREATIVES}</strong> commercials can run at the same time. On
        the dates below you have more — adjust the dates or remove a commercial.
      </p>
      <ul className="mt-1 list-disc pl-4 space-y-0.5">
        {overCapRanges.map((r) => (
          <li key={`${r.startDate}-${r.endDate}`}>
            {formatRange(r)} — {r.count} commercials
          </li>
        ))}
      </ul>
    </Banner>
  );
};

function formatRange(r: OverCapRange): string {
  const start = parseIso(r.startDate);
  const end = parseIso(r.endDate);
  if (!start || !end) return `${r.startDate} – ${r.endDate}`;
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
  if (r.startDate === r.endDate) return `${formatDate(start)} (1 day)`;
  return `${formatDate(start)} – ${formatDate(end)} (${days} days)`;
}

function parseIso(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export default ConcurrencyBanner;
